import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, MessageCirclePlus, MessageSquareWarning } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { RequestAlertCard } from "@/components/shared/RequestAlertCard";
import { ContactBlockedNotice } from "./contact-blocked-notice";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import type { ContactDetailKind, QuoteClarification } from "@/services/quoteRequestsService";

const QUESTION_MAX_LENGTH = 1000;
const QUESTION_MIN_LENGTH = 5;
const ANSWER_MAX_LENGTH = 2000;

export interface QuoteClarificationsCardProps {
  clarifications: QuoteClarification[];
  /** Professionals compose questions; the consumer composes answers. */
  viewer: "professional" | "consumer";
  /** False once the leg is closed: the thread stays readable, composing does not. */
  canCompose: boolean;
  /**
   * Controlled by the page, like QuoteSubmissionFields: only the page knows
   * whether a message was delivered or refused, and a blocked question must
   * survive so its author can edit it rather than retype it.
   */
  question?: string;
  onQuestionChange?: (value: string) => void;
  onAsk?: (question: string) => void;
  isAsking?: boolean;
  /**
   * Kinds the last attempt was blocked for, or null. Owned by the page because
   * the mutation that produces it lives there.
   */
  blockedKinds?: ContactDetailKind[] | null;
  onAnswer?: (clarificationId: string, answer: string) => void;
  /** Id of the question whose answer is in flight, so only that row disables. */
  answeringId?: string | null;
  /**
   * Let the card grow to the viewport height and scroll the thread inside it,
   * instead of growing the page. For use as a side rail, where the card has a
   * column to itself and the conversation is the only thing in it that has no
   * natural length.
   */
  fillHeight?: boolean;
}

/**
 * The public question thread on an RFQ leg (spec ce646966 §3a), rendered the
 * same way for both sides of it.
 *
 * One component rather than two because the thread IS the same thread: every
 * bidder and the consumer read identical content, and the only difference is
 * which end composes. Splitting it would be two places to keep the anonymity
 * wording consistent, and the wording is the feature.
 *
 * Laid out as a conversation, not a stack of form fields. An earlier pass gave
 * each entry its own full border inside the card's border and left the
 * composer permanently open below it, which read as three nested boxes and
 * made a one-question thread taller than the quote form underneath. Questions
 * and answers are now separated by indent and accent rather than by more
 * chrome, and the composer is a button until it is wanted.
 */
export function QuoteClarificationsCard({
  clarifications,
  viewer,
  canCompose,
  question = "",
  onQuestionChange,
  onAsk,
  isAsking = false,
  blockedKinds = null,
  onAnswer,
  answeringId = null,
  fillHeight = false,
}: QuoteClarificationsCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const [isComposing, setIsComposing] = useState(false);
  const threadScrollRef = useRef<HTMLDivElement | null>(null);

  // Past this many questions the thread list is capped and scrolls. Below it
  // the card grows with its content, which for almost every RFQ is a handful
  // of messages.
  const isLongThread = clarifications.length > 3;

  // Land on the newest message, once, on open. Only meaningful when the list
  // is capped — otherwise nothing is out of view. Deliberately mount-only:
  // re-scrolling on every update would yank the view while someone is reading
  // an earlier answer.
  useEffect(() => {
    if (!isLongThread && !fillHeight) return;
    const el = threadScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [isLongThread, fillHeight]);

  const isProfessional = viewer === "professional";
  const canAsk = isProfessional && canCompose && onAsk != null && onQuestionChange != null;

  if (clarifications.length === 0 && !canAsk) return null;

  const trimmed = question.trim();
  const canSubmitQuestion =
    trimmed.length >= QUESTION_MIN_LENGTH && trimmed.length <= QUESTION_MAX_LENGTH && !isAsking;

  // A refused message re-opens the composer: the text is still there to edit,
  // and collapsing it would hide both the explanation and what was written.
  const composerOpen = isComposing || blockedKinds != null;

  const handleAsk = (): void => {
    if (!canSubmitQuestion || !onAsk) return;
    onAsk(trimmed);
  };

  // A professional waiting on the consumer is the same kind of event as a
  // professional asking for a missing document, so it gets the same treatment
  // the request detail already uses for `missing_information` rather than a
  // second visual language for "someone needs something from you".
  const unansweredCount = clarifications.filter(
    (c) => c.answer == null && c.blocked == null,
  ).length;
  const needsConsumerAnswer = !isProfessional && canCompose && unansweredCount > 0;

  // Grouped per asking professional. Threads are private, so the consumer can
  // hold several unrelated conversations on one request; rendering them as one
  // flat list made two professionals asking different things read as a single
  // confused exchange.
  const threads = groupIntoThreads(clarifications);

  const body = (
    <>
      <p className="flex shrink-0 items-start gap-2 text-sm text-muted-foreground">
        <Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {t(
            isProfessional
              ? "requests.clarifications.introProfessional"
              : "requests.clarifications.introConsumer",
          )}
        </span>
      </p>

      {threads.length > 0 ? (
        // Capped and scrollable. Questions and answers run to 1000 and 2000
        // characters, and a consumer can hold a separate conversation with
        // every bidder — so an unbounded thread list grows without limit and
        // pushes the quote form (the thing this page exists for) off the
        // screen. The cap only engages once there is more than a screenful;
        // a two-message thread renders exactly as before.
        <div
          ref={threadScrollRef}
          // Two ways to be bounded. As a side rail (`fillHeight`) the card is
          // capped at the viewport and the thread takes whatever height is
          // left, so it grows to fill the column and scrolls rather than
          // growing the page. Otherwise the cap is CONDITIONAL on the thread
          // being long: `overflow-y-auto` renders scrollbar chrome on some
          // platforms with nothing to scroll, and a two-message thread showing
          // a scrollbar looks broken.
          className={cn(
            "space-y-3",
            fillHeight
              // A floor, not just a ceiling. Sized to content the flex item
              // landed exactly on its own height, so sub-pixel rounding was
              // enough to produce a scrollbar on a two-message thread — a
              // scrollbar with nothing to scroll reads as broken. 20rem also
              // gives the rail some presence next to a form four times its
              // height, instead of a card that stops a third of the way down.
              ? "min-h-[20rem] flex-1 overflow-y-auto overscroll-contain pr-1"
              : isLongThread && "max-h-[26rem] overflow-y-auto overscroll-contain pr-1",
          )}
        >
          {threads.map((thread) => (
            // Each conversation in its own container. Two threads separated by
            // nothing but a faint label ran together as one long exchange, and
            // the consumer could not tell where one professional stopped and
            // the next began.
            <section
              key={thread.key}
              className={
                threads.length > 1
                  ? "space-y-3 rounded-lg border border-border bg-background/60 p-3"
                  : "space-y-3"
              }
            >
              {/* A header only when there is more than one conversation to
                  tell apart. A professional has exactly one — their own — and
                  labelling it would be chrome around a single thing. */}
              {thread.ordinal != null && threads.length > 1 ? (
                <p className="border-b border-border pb-2 text-xs font-medium text-foreground">
                  {t("requests.clarifications.threadWith", { count: thread.ordinal })}
                </p>
              ) : null}
              {thread.items.map((c) => (
                <ClarificationThreadItem
                  key={c.id}
                  clarification={c}
                  viewer={viewer}
                  canCompose={canCompose}
                  onAnswer={onAnswer}
                  isAnswering={answeringId === c.id}
                />
              ))}
            </section>
          ))}
        </div>
      ) : null}

      {canAsk ? (
        // Its own zone, separated from the thread. Before, the collapsed
        // button hung directly under the last bubble with nothing tying it to
        // the card — it read as a stray control rather than the place you
        // write.
        <div
          className={cn(
            "shrink-0",
            threads.length > 0 && "border-t border-border pt-4",
          )}
        >
          {composerOpen ? (
            <div className="space-y-2">
              <Textarea
                id="quote-clarification-question"
                autoFocus
                value={question}
                onChange={(e) => onQuestionChange?.(e.target.value.slice(0, QUESTION_MAX_LENGTH))}
                placeholder={t("requests.clarifications.askPlaceholder")}
                rows={3}
                disabled={isAsking}
              />
              {blockedKinds ? <ContactBlockedNotice kinds={blockedKinds} /> : null}
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground">
                  {question.length > 0 ? `${question.length}/${QUESTION_MAX_LENGTH}` : ""}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    disabled={isAsking}
                    onClick={() => {
                      setIsComposing(false);
                      onQuestionChange?.("");
                    }}
                  >
                    {t("pages.common.cancel")}
                  </Button>
                  <Button onClick={handleAsk} disabled={!canSubmitQuestion}>
                    {t("requests.clarifications.sendButton")}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Shaped like the input it becomes, full width and aligned with the
            // card, rather than a floating button. Progressive disclosure still:
            // an always-open textarea made this card taller than the quote form
            // it sits beside, and a labelled field is not a hidden action.
            <button
              type="button"
              onClick={() => setIsComposing(true)}
              className="flex w-full items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <MessageCirclePlus className="h-4 w-4 shrink-0" aria-hidden />
              {t("requests.clarifications.askButton")}
            </button>
          )}
        </div>
      ) : null}
    </>
  );

  if (needsConsumerAnswer) {
    return (
      <RequestAlertCard
        tone="attention"
        icon={MessageSquareWarning}
        title={t("requests.clarifications.consumerAlertTitle", { count: unansweredCount })}
      >
        <div className="space-y-4">{body}</div>
      </RequestAlertCard>
    );
  }

  return (
    // Sticky as well as capped: a card limited to the viewport height is only
    // useful if it stays in view while the long column beside it scrolls past.
    <Card
      className={
        fillHeight
          ? "flex max-h-[calc(100vh-3rem)] flex-col lg:sticky lg:top-6"
          : undefined
      }
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {t("requests.clarifications.title")}
          {clarifications.length > 0 ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
              {clarifications.length}
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent
        className={cn("space-y-4", fillHeight && "flex min-h-0 flex-1 flex-col")}
      >
        {body}
      </CardContent>
    </Card>
  );
}

interface ClarificationThread {
  key: string;
  /** Anonymous handle for the asking professional; absent in a professional's own view. */
  ordinal: number | null;
  items: QuoteClarification[];
}

function groupIntoThreads(clarifications: QuoteClarification[]): ClarificationThread[] {
  if (clarifications.length === 0) return [];
  // A professional's list is entirely their own, so it carries no ordinal and
  // is one thread by definition.
  if (clarifications.every((c) => c.askerOrdinal == null)) {
    return [{ key: "mine", ordinal: null, items: clarifications }];
  }
  const byOrdinal = new Map<number, QuoteClarification[]>();
  for (const c of clarifications) {
    const ordinal = c.askerOrdinal ?? 0;
    const bucket = byOrdinal.get(ordinal);
    if (bucket) bucket.push(c);
    else byOrdinal.set(ordinal, [c]);
  }
  return [...byOrdinal.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ordinal, items]) => ({ key: `p${ordinal}`, ordinal, items }));
}

interface ClarificationThreadItemProps {
  clarification: QuoteClarification;
  viewer: "professional" | "consumer";
  canCompose: boolean;
  onAnswer?: (clarificationId: string, answer: string) => void;
  isAnswering: boolean;
}

function ClarificationThreadItem({
  clarification,
  viewer,
  canCompose,
  onAnswer,
  isAnswering,
}: ClarificationThreadItemProps): JSX.Element {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState("");

  const isProfessional = viewer === "professional";
  const isBlocked = clarification.blocked != null;
  // Answering a closed request asks the consumer to inform an auction nobody
  // can bid in any more, so the box goes when the window does.
  const canAnswer =
    !isProfessional && canCompose && clarification.answer == null && onAnswer != null;
  const trimmed = answer.trim();

  return (
    <div className="space-y-2">
      {/* Chat sides, by the usual rule: what you wrote sits right and tinted,
          what the other party wrote sits left and muted. The question is the
          professional's, so which side it lands on flips with the viewer. */}
      <MessageBubble
        own={isProfessional}
        text={clarification.question}
        at={clarification.createdAt}
      />

      {isBlocked ? (
        <ContactBlockedNotice kinds={clarification.blocked?.kinds ?? []} />
      ) : clarification.answer != null ? (
        <MessageBubble
          own={!isProfessional}
          text={clarification.answer}
          at={clarification.answeredAt ?? clarification.createdAt}
        />
      ) : canAnswer ? (
        // Full width, under the conversation it belongs to. A narrow box
        // pinned to the right read as a floating island rather than the place
        // you reply, and left the message it answers stranded across a gap.
        <div className="space-y-2 border-t border-border pt-3">
          <div>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value.slice(0, ANSWER_MAX_LENGTH))}
              placeholder={t("requests.clarifications.answerPlaceholder")}
              rows={2}
              disabled={isAnswering}
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                disabled={trimmed.length === 0 || isAnswering}
                // Deliberately not cleared on click. Moderation can refuse this
                // answer, and the copy tells the consumer to rewrite it — which
                // is impossible if submitting threw the text away. On success
                // the item re-renders answered and this box unmounts anyway.
                onClick={() => {
                  if (!onAnswer || trimmed.length === 0) return;
                  onAnswer(clarification.id, trimmed);
                }}
              >
                {t("requests.clarifications.answerButton")}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t(
            isProfessional
              ? "requests.clarifications.awaitingClient"
              : "requests.clarifications.pendingBadge",
          )}
        </p>
      )}
    </div>
  );
}

/** One message in a thread. Sides and tint say who wrote it; nothing else needs to. */
function MessageBubble({
  own,
  text,
  at,
}: {
  own: boolean;
  text: string;
  at: string;
}): JSX.Element {
  return (
    <div className={cn("flex", own ? "justify-end" : "justify-start")}>
      <div className="max-w-[min(32rem,85%)]">
        <div
          className={cn(
            // break-words, not just pre-line: an unbroken 200-character
            // string (a pasted URL, a run-together sentence) has no wrap
            // opportunity and overflowed the bubble horizontally.
            "rounded-2xl px-3 py-2 text-sm whitespace-pre-line break-words",
            own
              ? "rounded-br-sm bg-primary/15 text-foreground"
              : "rounded-bl-sm bg-muted text-foreground",
          )}
        >
          {text}
        </div>
        <p
          className={cn(
            "mt-0.5 text-[11px] leading-none text-muted-foreground",
            own ? "text-right" : "text-left",
          )}
        >
          {formatDisplayDate(at)}
        </p>
      </div>
    </div>
  );
}

/**
 * What moderation stopped, and why. Never silent (§3c): a message that
 * disappeared without explanation teaches people the product is broken.
 */
